import { Authenticated, Refine } from "@refinedev/core";
import routerProvider from "@refinedev/react-router";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useParams } from "react-router";

import { AppLayout } from "@/components/layout/AppLayout";
import { HOME, loginPath } from "@/lib/loginReturn";
import { authProvider, dataProvider } from "@/lib/providers";
import { AllReferencesPage } from "@/pages/AllReferencesPage";
import { AnalyticsPage, AnalyticsReportPage } from "@/pages/AnalyticsPage";
import { BasketPage } from "@/pages/BasketPage";
import { CatalogPage } from "@/pages/CatalogPage";
import { FittingPage } from "@/pages/FittingPage";
import { InteriorStatsPage } from "@/pages/InteriorStatsPage";
import { InteriorsSwipePage } from "@/pages/InteriorsSwipePage";
import { DressPage } from "@/pages/DressPage";
import { CallsPage } from "@/pages/CallsPage";
import { LibrariesPage } from "@/pages/LibrariesPage";
import { LibraryPage } from "@/pages/LibraryPage";
import { LoginPage } from "@/pages/LoginPage";
import { PersonaPage } from "@/pages/PersonaPage";
import { PersonasPage } from "@/pages/PersonasPage";
import { PhotosPage } from "@/pages/PhotosPage";
import { PinPage } from "@/pages/PinPage";
import { ProjectPage } from "@/pages/ProjectPage";
import { ProjectsPage } from "@/pages/ProjectsPage";
import { ReferencePage } from "@/pages/ReferencePage";
import { ResultCardPage } from "@/pages/ResultCardPage";
import { PostPlanPage } from "@/pages/PostPlanPage";
import { PostsPage } from "@/pages/PostsPage";
import { ResultsComparePage } from "@/pages/ResultsComparePage";
import { ResultsPage } from "@/pages/ResultsPage";
import { ShotPage } from "@/pages/ShotPage";
import { TrendTopicPage } from "@/pages/TrendTopicPage";
import { TrendTopicRefsPage } from "@/pages/TrendTopicRefsPage";
import { TrendTypePage } from "@/pages/TrendTypePage";
import { TrendsPage } from "@/pages/TrendsPage";
import { VideoRefPage } from "@/pages/VideoRefPage";
import { VideoRefsPage } from "@/pages/VideoRefsPage";

/**
 * Адреса проектов и библиотек — те же, что у прежней htmx-панели (`/project/1`,
 * `/lib/2`): закладки менеджера переживают переезд на React.
 */
export function App() {
  return (
    <BrowserRouter>
      <Refine
        dataProvider={dataProvider}
        authProvider={authProvider}
        routerProvider={routerProvider}
        options={{ disableTelemetry: true }}
      >
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          {/* Лента интерьеров на телефоне: под тем же входом, но без меню и боковой панели. */}
          <Route
            path="/m/interiors"
            element={
              <Authenticated key="m-interiors" fallback={<Navigate to={loginPath()} replace />}>
                <InteriorsSwipePage />
              </Authenticated>
            }
          />
          <Route
            element={
              <Authenticated key="app" fallback={<Navigate to={loginPath()} replace />}>
                <AppLayout>
                  <Outlet />
                </AppLayout>
              </Authenticated>
            }
          >
            <Route index element={<Navigate to={HOME} replace />} />
            <Route path="/trends" element={<TrendsPage />} />
            <Route path="/trends/:topicId" element={<TrendTopicPage />} />
            <Route path="/trends/:topicId/refs" element={<TrendTopicRefsPage />} />
            <Route path="/trends/type/:typeId" element={<TrendTypePage />} />
            <Route path="/video-refs" element={<VideoRefsPage />} />
            <Route path="/video-refs/:refId" element={<VideoRefPage />} />
            <Route path="/pins/:pinId" element={<PinPage />} />
            <Route path="/photos" element={<PhotosPage />} />
            <Route path="/generate" element={<FittingPage />} />
            <Route path="/catalog" element={<CatalogPage />} />
            <Route path="/photos/:dressId" element={<DressPage />} />
            <Route path="/personas" element={<PersonasPage />} />
            <Route path="/persona/:personaId" element={<PersonaPage />} />
            <Route path="/calls" element={<CallsPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/analytics/:slug" element={<AnalyticsReportPage />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/project/:projectId" element={<ProjectPage />} />
            <Route path="/project/:projectId/shot/:shotId" element={<ShotPage />} />
            <Route path="/p/:projectId" element={<LegacyProject />} />
            <Route path="/results" element={<ResultsPage />} />
            <Route path="/results/compare" element={<ResultsComparePage />} />
            <Route path="/results/:variantId" element={<ResultCardPage />} />
            <Route path="/posts" element={<PostsPage />} />
            <Route path="/posts/:planId" element={<PostPlanPage />} />
            <Route path="/lib" element={<LibrariesPage />} />
            <Route path="/lib/all" element={<AllReferencesPage />} />
            <Route path="/lib/interiors" element={<InteriorStatsPage />} />
            <Route path="/lib/basket" element={<BasketPage />} />
            <Route path="/lib/ref/:itemId" element={<ReferencePage />} />
            <Route path="/lib/:libraryId" element={<LibraryPage />} />
            <Route path="*" element={<Navigate to={HOME} replace />} />
          </Route>
        </Routes>
      </Refine>
    </BrowserRouter>
  );
}

/** Старый адрес проекта `/p/1` — закладка обязана продолжать работать. */
function LegacyProject() {
  const { projectId } = useParams();
  return <Navigate to={`/project/${projectId ?? ""}`} replace />;
}
